import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { getProItemLedger } from '../../../../Store/prosafe/proReport/getProItemLedger';
import Sidebar from '../../Include/Sidebar';
import Header from '../../Include/Header';
import Footer from '../../Include/Footer';
import Loader from '../../Include/Loader';
import PageHeader from '../../Components/PageHeader/PageHeader';
import moment from 'moment';
import useDebounce from '../../../../hooks/useDebounce';

const ProItemLedger = () => {
    const dispatch = useDispatch();
    const [search, setSearch] = useState("");
    const [limit, setlimit] = useState(10);
    const [filter, setFilter] = useState({
        startDate: "",
        endDate: ""
    });
    const [activeItem, setActiveItem] = useState(null);

    const debouncedSearch = useDebounce(search, 400);
    const debouncedStartDate = useDebounce(filter.startDate, 300);
    const debouncedEndDate = useDebounce(filter.endDate, 300);

    const entity = useSelector((state) => state?.getProItemLedger?.user?.data);
    const isLoading = useSelector((state) => state?.getProItemLedger?.loading);

    const fetchData = () => {
        const formData = new URLSearchParams();
        formData.append('filter', JSON.stringify({
            startDate: debouncedStartDate || null,
            endDate: debouncedEndDate || null,
            date: {
                start: debouncedStartDate || null,
                end: debouncedEndDate || null
            }
        }));
        formData.append('search', debouncedSearch || "");
        formData.append('firm_id', localStorage.getItem('PAY_USER_FIRM_ID') || "");
        formData.append('year_id', localStorage.getItem('PAY_USER_YEAR_ID') || "");
        dispatch(getProItemLedger({ formData }));
    };

    useEffect(() => {
        fetchData();
    }, [debouncedSearch, debouncedStartDate, debouncedEndDate]);

    const commentsData = useMemo(() => {
        if (!entity || !Array.isArray(entity)) return [];
        return entity;
    }, [entity]);

    useEffect(() => {
        if (commentsData.length > 0) {
            if (!activeItem || !commentsData.some(it => String(it.item_id) === String(activeItem.item_id))) {
                setActiveItem(commentsData[0]);
            } else {
                const updated = commentsData.find(it => String(it.item_id) === String(activeItem.item_id));
                if (updated) {
                    setActiveItem(updated);
                }
            }
        } else {
            setActiveItem(null);
        }
    }, [commentsData]);

    const handleRefresh = () => {
        setSearch("");
        setFilter({ startDate: "", endDate: "" });
    };

    const handleFilterChange = (newFilter) => {
        setFilter(newFilter);
    };

    const handleItemClick = (item) => {
        setActiveItem(item);
    };

    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const handleOpen = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    const filterConfig = [
        {
            key: "startDate",
            label: "Start Date",
            type: "date",
            value: filter.startDate,
            placeholder: "Select Start Date",
        },
        {
            key: "endDate",
            label: "End Date",
            type: "date",
            value: filter.endDate,
            placeholder: "Select End Date",
        },
    ];

    // Flatten transactions for activeItem in chronological order
    const transactions = useMemo(() => {
        if (!activeItem?.order_details || !Array.isArray(activeItem.order_details)) return [];
        const list = [];
        activeItem.order_details.forEach(orderDetail => {
            orderDetail?.tag_details?.forEach(tag => {
                tag?.trans_details?.forEach(trans => {
                    list.push({
                        date: orderDetail.trans_date,
                        tagName: tag.tag_name,
                        voucherNo: trans.voucher_no,
                        billNo: trans.bill_no || '-',
                        partyName: trans.party_name || '-',
                        inQty: Number(trans.item_details?.in) || 0,
                        outQty: Number(trans.item_details?.out) || 0,
                    });
                });
            });
        });

        list.sort((a, b) => {
            const dateA = new Date(a.date).getTime();
            const dateB = new Date(b.date).getTime();
            if (dateA !== dateB) return dateA - dateB;
            return String(a.voucherNo).localeCompare(String(b.voucherNo));
        });

        return list;
    }, [activeItem]);

    let runningBalance = activeItem ? (Number(activeItem.opening_balance) || 0) : 0;
    let grandTotalIn = 0;
    let grandTotalOut = 0;

    const computedRows = transactions.map((t, idx) => {
        runningBalance += (t.inQty - t.outQty);
        grandTotalIn += t.inQty;
        grandTotalOut += t.outQty;
        return {
            ...t,
            runningBal: runningBalance,
            key: idx
        };
    });

    return (
        <div className={`main-wrapper ${isSidebarOpen ? 'slide-nav' : ''}`}>
            <Header handleOpen={handleOpen} />
            <Sidebar />

            <div className="page-wrapper">
                <div className="content">
                    <div className="page-header">
                        <div className="row">
                            <div className="col-sm-12">
                                <ul className="breadcrumb">
                                    <li className="breadcrumb-item"><Link to="/main-store/user/dashboard">Dashboard </Link></li>
                                    <li className="breadcrumb-item"><i className="feather-chevron-right"></i></li>
                                    <li className="breadcrumb-item active">Item Ledger List</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-sm-12">
                            <div className="card card-table show-entire">
                                <div className="card-body">
                                    <PageHeader
                                        title="Item Ledger List"
                                        onSearch={(value) => setSearch(value)}
                                        onRefresh={handleRefresh}
                                        filter={filter}
                                        onFilterChange={handleFilterChange}
                                        limit={limit}
                                        onLimitChange={(val) => setlimit(val)}
                                        filterConfig={filterConfig}
                                    />
                                </div>
                            </div>

                            <div className="col-sm-12">
                                <div className='row'>
                                    <div className='col-md-4 col-sm-12'>
                                        <div className="card card-table show-entire itemLedgerCard">
                                            <div className="card-body" style={{ minHeight: '350px' }}>
                                                <div className="nav flex-column nav-pills ledger-navs" role="tablist" aria-orientation="vertical">
                                                    <h4>Item</h4>
                                                    {isLoading && commentsData.length === 0 ? (
                                                        <div className="d-flex justify-content-center align-items-center py-5">
                                                            <Loader />
                                                        </div>
                                                    ) : (
                                                        <>
                                                            {commentsData?.map((it, i) => (
                                                                <div className='d-flex' key={it?.item_id || i}>
                                                                    <button
                                                                        className={`nav-link w-100 ${activeItem?.item_id === it.item_id ? 'active' : ''}`}
                                                                        onClick={() => handleItemClick(it)}
                                                                        style={{
                                                                            color: activeItem?.item_id === it.item_id ? '' : '#7b7a7a',
                                                                            textAlign: 'left'
                                                                        }}>
                                                                        {it?.item_name}
                                                                    </button>
                                                                    {/* {activeItem?.item_id === it.item_id && (
                                                                        <button type='button' className={`nav-link btn btn-sm mx-1 ${activeItem?.item_id === it.item_id ? 'active' : ''}`}
                                                                            onClick={() => downloadPdf(it)}
                                                                            style={{
                                                                                color: activeItem?.item_id === it.item_id ? '' : '#7b7a7a', textAlign: 'left'
                                                                            }}><i
                                                                                className="fa-solid fa-download"></i></button>
                                                                    )
                                                                    } */}
                                                                </div>
                                                            ))}
                                                            {commentsData?.length === 0 && (
                                                                <div className='d-flex align-items-center justify-content-center py-5'>
                                                                    <h5>No Items Found</h5>
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className='col-md-8 col-sm-12'>
                                        <div className="card card-table show-entire itemLedgerCard">
                                            <div className="card-body" style={{ minHeight: '350px' }}>
                                                {isLoading ? (
                                                    <div className="d-flex justify-content-center align-items-center py-5">
                                                        <Loader />
                                                    </div>
                                                ) : (
                                                    <div className="table-responsive">
                                                        <table className="table border-0 custom-table comman-table mb-0">
                                                            <thead>
                                                                <tr>
                                                                    <th>Date</th>
                                                                    <th>Type</th>
                                                                    <th>Voucher No.</th>
                                                                    <th>Bill No.</th>
                                                                    <th>Party Name</th>
                                                                    <th style={{ textAlign: 'right' }}>In Qty.</th>
                                                                    <th style={{ textAlign: 'right' }}>Out Qty.</th>
                                                                    <th style={{ textAlign: 'right' }}>Bal. Qty.</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {(commentsData.length === 0 || !activeItem) ? (
                                                                    <tr>
                                                                        <td colSpan="8">
                                                                            <div className="no-table-data">No Data Found!</div>
                                                                        </td>
                                                                    </tr>
                                                                ) : (
                                                                    <>
                                                                        <tr>
                                                                            <td colSpan="8" style={{ fontWeight: 'bold', textAlign: 'left', backgroundColor: '#fcfcfc' }}>
                                                                                {activeItem.item_name} {activeItem.unit ? `( ${activeItem.unit} )` : ''}
                                                                            </td>
                                                                        </tr>
                                                                        <tr>
                                                                            <td colSpan="8" style={{ textAlign: 'left', fontStyle: 'italic' }}>
                                                                                OP Balance: {(Number(activeItem.opening_balance) || 0).toFixed(3)}
                                                                            </td>
                                                                        </tr>

                                                                        {computedRows.map((row) => (
                                                                            <tr key={row.key}>
                                                                                <td>{moment(row.date).format('YYYY-MM-DD')}</td>
                                                                                <td>{row.tagName}</td>
                                                                                <td>{row.voucherNo}</td>
                                                                                <td>{row.billNo}</td>
                                                                                <td>{row.partyName}</td>
                                                                                <td style={{ textAlign: 'right' }}>{row.inQty.toFixed(3)}</td>
                                                                                <td style={{ textAlign: 'right' }}>{row.outQty.toFixed(3)}</td>
                                                                                <td style={{ textAlign: 'right', color: row.runningBal < 0 ? 'red' : 'inherit', fontWeight: '500' }}>
                                                                                    {row.runningBal.toFixed(3)}
                                                                                </td>
                                                                            </tr>
                                                                        ))}

                                                                        {computedRows.length === 0 && (
                                                                            <tr>
                                                                                <td colSpan="8" className="text-center py-3 text-muted">
                                                                                    No transactions found for this item in the selected period
                                                                                </td>
                                                                            </tr>
                                                                        )}

                                                                        <tr style={{ height: '50px', backgroundColor: '#f8f9fa' }}>
                                                                            <td colSpan="5" style={{ textAlign: 'right', fontWeight: 'bold' }}>Grand Total</td>
                                                                            <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{grandTotalIn.toFixed(3)}</td>
                                                                            <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{grandTotalOut.toFixed(3)}</td>
                                                                            <td style={{ textAlign: 'right', fontWeight: 'bold', color: runningBalance < 0 ? 'red' : 'inherit' }}>
                                                                                {runningBalance.toFixed(3)}
                                                                            </td>
                                                                        </tr>
                                                                    </>
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>
        </div>
    );
};

export default ProItemLedger;